import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenants, useCreateTenant } from "@/hooks/useTenants";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icons";
import {
  StatusBadge,
  DeployBadge,
  TrialBadge,
  OriginBadge,
} from "@/components/ui/status-badges";
import {
  PageHead,
  Btn,
  NativeSelect,
  Field,
  Modal,
} from "@/components/ui/page-components";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { Tenant } from "@/types";

const VENUE_LABEL: Record<string, string> = {
  simulator: "Simulator",
  golf_course: "Golf Course",
};

/* ---------- Filter Chip ---------- */
function FilterChip({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors",
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-muted-foreground hover:bg-muted",
      )}
    >
      {children}
      {count != null && (
        <span
          className={cn(
            "rounded-full px-1.5 text-[10px]",
            active ? "bg-primary/20" : "bg-muted",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/* ---------- Checkbox ---------- */
function Check({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      className={cn(
        "flex h-4 w-4 items-center justify-center rounded border transition-colors",
        checked
          ? "border-primary bg-primary text-white"
          : "border-input bg-card hover:border-primary",
      )}
    >
      {checked && <Icon name="check" size={11} />}
    </button>
  );
}

/* ---------- Tenant Card (grid view) ---------- */
function TenantCard({
  tenant,
  onSelect,
  selected,
  navigate,
}: {
  tenant: Tenant;
  onSelect: () => void;
  selected: boolean;
  navigate: (path: string) => void;
}) {
  return (
    <Card
      className={cn(
        "group relative cursor-pointer p-4 transition-all hover:shadow-md hover:-translate-y-0.5",
        selected && "ring-2 ring-primary",
      )}
      onClick={() => navigate(`/tenants/${tenant.id}`)}
    >
      <div
        className="absolute right-3 top-3"
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
      >
        <Check checked={selected} onChange={onSelect} />
      </div>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary">
          <Icon name="building" size={18} />
        </span>
        <div className="min-w-0 pr-6">
          <div className="truncate font-bold text-foreground">
            {tenant.name}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="truncate text-[11px] text-muted-foreground">
              {tenant.domain || `${tenant.slug}.teelogix.io`}
            </span>
            <OriginBadge tenant={tenant} className="shrink-0" />
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <StatusBadge status={tenant.status} />
        <DeployBadge status={tenant.deployment_status} />
        <TrialBadge tenant={tenant} />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-border/60 pt-3">
        <div className="text-[11px] text-muted-foreground">{tenant.slug}</div>
        {tenant.account_number && (
          <div className="text-[11px] font-mono font-semibold text-muted-foreground">
            #{tenant.account_number}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ---------- Page ---------- */
export default function TenantsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [deploy, setDeploy] = useState("all");
  const [venue, setVenue] = useState("all");
  const [view, setView] = useState<"table" | "grid">("table");
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: "",
    slug: "",
    domain: "",
    venue_types: [] as string[],
    is_private: false,
  });

  const { data: tenants = [], isLoading } = useTenants();
  const createMutation = useCreateTenant();

  // ?new=1 from dashboard quick action
  useEffect(() => {
    if (searchParams.get("new") === "1") setDialogOpen(true);
  }, [searchParams]);

  const filtered = useMemo(
    () =>
      tenants.filter((t) => {
        if (
          search &&
          !(
            t.name.toLowerCase().includes(search.toLowerCase()) ||
            t.slug.includes(search.toLowerCase()) ||
            (t.domain || "").includes(search.toLowerCase())
          )
        )
          return false;
        if (status !== "all" && t.status !== status) return false;
        if (deploy !== "all" && t.deployment_status !== deploy) return false;
        if (venue !== "all" && !(t.venue_types || []).includes(venue))
          return false;
        return true;
      }),
    [tenants, search, status, deploy, venue],
  );

  const counts = useMemo(() => {
    const c = { active: 0, provisioning: 0, suspended: 0, archived: 0 };
    tenants.forEach((t) => {
      if (c[t.status as keyof typeof c] !== undefined)
        c[t.status as keyof typeof c]++;
    });
    return c;
  }, [tenants]);

  const toggle = (id: string | number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSel =
    filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const toggleAll = () =>
    setSelected(allSel ? new Set() : new Set(filtered.map((t) => t.id)));
  const clearSel = () => setSelected(new Set());

  const hasFilters =
    status !== "all" || deploy !== "all" || venue !== "all" || !!search;

  const handleCreate = async () => {
    if (!newTenant.name || !newTenant.slug) return;
    try {
      const tenant = await createMutation.mutateAsync(newTenant);
      toast.success(`Tenant "${tenant.name}" created`);
      setDialogOpen(false);
      setNewTenant({
        name: "",
        slug: "",
        domain: "",
        venue_types: [],
        is_private: false,
      });
      navigate(`/tenants/${tenant.id}`);
    } catch {
      toast.error("Failed to create tenant");
    }
  };

  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    setNewTenant((prev) => ({ ...prev, name, slug }));
  };

  const toggleVenue = (v: string) =>
    setNewTenant((f) => ({
      ...f,
      venue_types: f.venue_types.includes(v)
        ? f.venue_types.filter((x) => x !== v)
        : [...f.venue_types, v],
    }));

  return (
    <div className="space-y-4">
      <PageHead
        title="Tenants"
        sub={`${tenants.length} venue deployments managed`}
        action={
          <Btn icon="plus" onClick={() => setDialogOpen(true)}>
            Add tenant
          </Btn>
        }
      />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1 max-w-xs">
          <Icon
            name="search"
            size={15}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, slug, domain…"
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterChip
            active={status === "all"}
            onClick={() => setStatus("all")}
          >
            All
          </FilterChip>
          <FilterChip
            active={status === "active"}
            onClick={() => setStatus("active")}
            count={counts.active}
          >
            Active
          </FilterChip>
          <FilterChip
            active={status === "provisioning"}
            onClick={() => setStatus("provisioning")}
            count={counts.provisioning}
          >
            Provisioning
          </FilterChip>
          <FilterChip
            active={status === "suspended"}
            onClick={() => setStatus("suspended")}
            count={counts.suspended}
          >
            Suspended
          </FilterChip>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <NativeSelect
            value={deploy}
            onChange={setDeploy}
            className="w-[150px]"
          >
            <option value="all">Any deploy state</option>
            <option value="deployed">Deployed</option>
            <option value="deploying">Deploying</option>
            <option value="failed">Failed</option>
            <option value="draft">Draft</option>
          </NativeSelect>
          <NativeSelect value={venue} onChange={setVenue} className="w-[140px]">
            <option value="all">Any venue type</option>
            <option value="simulator">Simulator</option>
            <option value="golf_course">Golf course</option>
          </NativeSelect>
          <div className="flex rounded-lg border border-border">
            <button
              onClick={() => setView("table")}
              className={cn(
                "px-2 py-1.5 rounded-l-lg",
                view === "table"
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              <Icon name="table" size={16} />
            </button>
            <button
              onClick={() => setView("grid")}
              className={cn(
                "px-2 py-1.5 rounded-r-lg",
                view === "grid"
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/50",
              )}
            >
              <Icon name="layout" size={16} />
            </button>
          </div>
        </div>
      </div>

      {hasFilters && (
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <span>
            {filtered.length} of {tenants.length} shown
          </span>
          <button
            onClick={() => {
              setSearch("");
              setStatus("all");
              setDeploy("all");
              setVenue("all");
            }}
            className="font-semibold text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : view === "grid" ? (
        /* Grid view */
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((t) => (
            <TenantCard
              key={t.id}
              tenant={t}
              navigate={navigate}
              selected={selected.has(t.id)}
              onSelect={() => toggle(t.id)}
            />
          ))}
          {filtered.length === 0 && <EmptyState />}
        </div>
      ) : (
        /* Table view */
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="w-10 px-3 py-2.5">
                    <Check checked={allSel} onChange={toggleAll} />
                  </th>
                  <th className="px-3 py-2.5">Venue</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Deploy</th>
                  <th className="px-3 py-2.5">Plan</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => navigate(`/tenants/${t.id}`)}
                    className={cn(
                      "group cursor-pointer border-b border-border/60 last:border-0 transition-colors hover:bg-muted/50",
                      selected.has(t.id) && "bg-primary/5",
                    )}
                  >
                    <td
                      className="px-3 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Check
                        checked={selected.has(t.id)}
                        onChange={() => toggle(t.id)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary">
                          <Icon name="building" size={15} />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-foreground">
                            {t.name}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-[11px] text-muted-foreground">
                              {t.domain || `${t.slug}.teelogix.io`}
                            </span>
                            <OriginBadge tenant={t} className="shrink-0" />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge status={t.status} />
                    </td>
                    <td className="px-3 py-2.5">
                      <DeployBadge status={t.deployment_status} />
                    </td>
                    <td className="px-3 py-2.5">
                      <TrialBadge tenant={t} />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <EmptyState />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 shadow-2xl">
          <span className="px-1.5 text-[13px] font-semibold text-foreground">
            {selected.size} selected
          </span>
          <span className="h-5 w-px bg-border" />
          <Btn variant="ghost" size="sm" icon="rocket">
            Deploy
          </Btn>
          <Btn variant="ghost" size="sm" icon="power">
            Suspend
          </Btn>
          <Btn
            variant="ghost"
            size="sm"
            icon="trash"
            className="!text-red-600 hover:!bg-red-50"
          >
            Delete
          </Btn>
          <span className="h-5 w-px bg-border" />
          <button
            onClick={clearSel}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Add new tenant"
        description="Create a new venue deployment entry."
        footer={
          <>
            <Btn variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Btn>
            <Btn
              disabled={
                !newTenant.name || !newTenant.slug || createMutation.isPending
              }
              onClick={handleCreate}
            >
              {createMutation.isPending ? "Creating…" : "Create tenant"}
            </Btn>
          </>
        }
      >
        <div className="space-y-3.5 py-1">
          <Field label="Venue name">
            <Input
              value={newTenant.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Mulligan Harbor Golf"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slug">
              <Input
                value={newTenant.slug}
                onChange={(e) =>
                  setNewTenant({ ...newTenant, slug: e.target.value })
                }
                placeholder="mulligan-harbor"
              />
            </Field>
            <Field label="Domain (optional)">
              <Input
                value={newTenant.domain}
                onChange={(e) =>
                  setNewTenant({ ...newTenant, domain: e.target.value })
                }
                placeholder="venue.com"
              />
            </Field>
          </div>
          <Field label="Venue types">
            <div className="flex gap-2">
              {(["simulator", "golf_course"] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => toggleVenue(v)}
                  className={cn(
                    "flex flex-1 items-center gap-2 rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors",
                    newTenant.venue_types.includes(v)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  <Icon
                    name={v === "simulator" ? "monitor" : "flag"}
                    size={15}
                  />
                  {VENUE_LABEL[v]}
                  {newTenant.venue_types.includes(v) && (
                    <Icon name="check" size={13} className="ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
            <div>
              <div className="text-[12px] font-semibold text-foreground">
                Private venue
              </div>
              <div className="text-[11px] text-muted-foreground">
                {newTenant.is_private ? "Members only" : "Open to public"}
              </div>
            </div>
            <Switch
              checked={newTenant.is_private}
              onCheckedChange={(checked) =>
                setNewTenant({ ...newTenant, is_private: checked })
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Icon name="search" size={22} />
      </span>
      <p className="text-[13px] font-medium text-foreground">
        No tenants match your filters
      </p>
      <p className="text-[12px] text-muted-foreground">
        Try adjusting your search or clearing filters.
      </p>
    </div>
  );
}
